import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Search, Menu,  BookOpen, Feather, ExternalLink } from 'lucide-react';

const BrownUniversity: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Brown University";

    return (
        <div className="min-h-screen bg-white font-sans text-[#4E3629] selection:bg-[#ED1C24] selection:text-white">
            {/* Header */}
            <header className="bg-white py-8 px-6 md:px-12 sticky top-0 z-50 border-b border-gray-100">
                <div className="max-w-[1400px] mx-auto flex justify-between items-end md:items-center">
                    <div className="flex flex-col">
                        <span className="font-serif text-4xl md:text-5xl text-[#4E3629] leading-none mb-1">{schoolName.split(' ')[0].toUpperCase()}</span>
                    </div>

                    <div className="hidden lg:flex gap-10 font-sans font-bold text-[#4E3629] text-sm tracking-wide">
                        <a href="#" className="hover:text-[#ED1C24] transition-colors">About {schoolName.split(' ')[0]}</a>
                        <a href="#" className="hover:text-[#ED1C24] transition-colors">Admissions</a>
                        <a href="#" className="hover:text-[#ED1C24] transition-colors">Academics</a>
                        <a href="#" className="hover:text-[#ED1C24] transition-colors">Research</a>
                        <a href="#" className="hover:text-[#ED1C24] transition-colors">Campus Life</a>
                    </div>

                    <div className="flex gap-6 items-center">
                        <span className="font-bold text-xs uppercase tracking-widest hidden md:block text-[#999]">Providence, RI</span>
                        <Search className="text-[#4E3629] cursor-pointer hover:text-[#ED1C24]" size={22} />
                        <Menu className="text-[#4E3629] cursor-pointer lg:hidden" size={24} />
                    </div>
                </div>
            </header>

            {/* Hero - The Open Curriculum Vibe */}
            <section className="relative h-[650px] bg-[#fdfdfd]">
                 <div className="grid grid-cols-1 lg:grid-cols-2 h-full">
                     <div className="p-12 md:p-24 flex flex-col justify-center">
                         <span className="text-[#ED1C24] font-bold uppercase tracking-widest text-sm mb-6">The Open Curriculum</span>
                         <h1 className="text-5xl md:text-7xl font-light text-[#4E3629] mb-8 leading-tight">
                             Forge your own path.
                         </h1>
                         <p className="text-xl text-gray-600 mb-12 max-w-lg leading-relaxed font-light">
                             At {schoolName.split(' ')[0]}, students design their own course of study, becoming the architects of their own education.
                         </p>
                         <Button className="bg-[#4E3629] hover:bg-[#32231a] text-white font-bold px-10 h-14 rounded-full text-lg w-fit">
                             Plan Your Visit
                         </Button>
                     </div>
                     <div className="relative h-full">
                         <img 
                            src="https://images.unsplash.com/photo-1623631559868-b77054aeec65?q=80&w=2070&auto=format&fit=crop" 
                            alt="Brown Campus" 
                            className="absolute inset-0 w-full h-full object-cover"
                         />
                     </div>
                 </div>
            </section>

            {/* Quick Facts / Gateway */}
            <section className="bg-[#4E3629] text-white py-20 px-6 md:px-12">
                <div className="max-w-[1400px] mx-auto text-center">
                    <h2 className="text-3xl font-serif mb-16">Distinction & Diversity</h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                        <div className="p-8 border border-white/20 hover:bg-white/5 transition-colors cursor-pointer group">
                             <div className="text-5xl font-light mb-4 text-[#ED1C24] group-hover:scale-110 transition-transform inline-block">7:1</div>
                             <h3 className="text-xl font-bold mb-2">Student-Faculty Ratio</h3>
                             <p className="text-gray-400 text-sm">Close mentorship with world-class scholars.</p>
                        </div>
                         <div className="p-8 border border-white/20 hover:bg-white/5 transition-colors cursor-pointer group">
                             <div className="text-5xl font-light mb-4 text-[#ED1C24] group-hover:scale-110 transition-transform inline-block">80+</div>
                             <h3 className="text-xl font-bold mb-2">Concentrations</h3>
                             <p className="text-gray-400 text-sm">Rigorous, multidisciplinary fields of study.</p>
                        </div>
                         <div className="p-8 border border-white/20 hover:bg-white/5 transition-colors cursor-pointer group">
                             <div className="text-5xl font-light mb-4 text-[#ED1C24] group-hover:scale-110 transition-transform inline-block">100%</div>
                             <h3 className="text-xl font-bold mb-2">Demonstrated Need Met</h3>
                             <p className="text-gray-400 text-sm">Committed to making education accessible.</p>
                        </div>
                    </div>
                </div>
            </section>

             {/* News List */}
             <section className="py-24 px-6 md:px-12 bg-white max-w-[1000px] mx-auto">
                 <h2 className="text-3xl font-serif text-[#4E3629] mb-12 border-b border-gray-200 pb-4">Headlines</h2>

                 <div className="space-y-12">
                     {[
                         { date: "Oct 22, 2025", title: "New School of Public Health launched to address global challenges", cat: "University News" },
                         { date: "Oct 18, 2025", title: `${schoolName.split(' ')[0]} researchers synthesize new material for quantum computing`, cat: "Research" },
                         { date: "Oct 14, 2025", title: "Arts initiative brings immersive theater to College Green", cat: "Arts & Culture" }
                     ].map((news, i) => (
                         <div key={i} className="group cursor-pointer">
                             <div className="flex justify-between items-center mb-2">
                                 <span className="text-[#ED1C24] font-bold text-xs uppercase tracking-widest">{news.cat}</span>
                                 <span className="text-gray-400 text-xs">{news.date}</span>
                             </div>
                             <h3 className="text-2xl font-bold text-[#4E3629] mb-2 group-hover:text-[#ED1C24] transition-colors leading-tight">
                                 {news.title}
                             </h3>
                             <div className="flex items-center gap-1 text-sm font-bold text-[#999] group-hover:text-[#4E3629] transition-colors">
                                 Read More <ExternalLink size={14} />
                             </div>
                         </div>
                     ))}
                 </div>
             </section>

            {/* Footer */}
            <footer className="bg-[#f0f0f0] text-[#4E3629] py-16 px-6 md:px-12 border-t border-gray-200">
                <div className="max-w-[1400px] mx-auto flex flex-col md:flex-row justify-between gap-12">
                    <div>
                        <span className="font-serif text-3xl font-bold block mb-6">MUSEUM</span> 
                         {/* Note: Playing on Brown's specific logo style often just text */}
                         <p className="font-serif text-3xl block mb-6">{schoolName.split(' ')[0].toUpperCase()}</p>

                         <p className="text-sm text-gray-600 max-w-xs">
                             Providence, Rhode Island 02912 <br/>
                             401-863-1000
                         </p>
                    </div>

                    <div className="grid grid-cols-2 gap-12 text-sm font-bold opacity-80">
                         <ul className="space-y-3">
                             <li><a href="#" className="hover:text-[#ED1C24]">A-Z Index</a></li>
                             <li><a href="#" className="hover:text-[#ED1C24]">Directory</a></li>
                             <li><a href="#" className="hover:text-[#ED1C24]">Contact Us</a></li>
                         </ul>
                         <ul className="space-y-3">
                             <li><a href="#" className="hover:text-[#ED1C24]">Accessibility</a></li>
                             <li><a href="#" className="hover:text-[#ED1C24]">Privacy Policy</a></li>
                             <li><a href="#" className="hover:text-[#ED1C24]">Emergency</a></li>
                         </ul>
                    </div>
                </div>
                <div className="max-w-[1400px] mx-auto mt-12 text-center text-xs text-gray-400">
                    © 2026 {schoolName}
                </div>
            </footer>
        </div>
    );
};

export default BrownUniversity;
